import { useMemo } from 'react'
import { useQuery, useQueries, useInfiniteQuery } from 'react-query'
import { fetchClientsList, fetchClientById } from '../services/clients.service'
import { getClient, getEquipmentAssignmentsByClientId } from '../services/check-in/clients.service'

/**
 * Hook to fetch clients list from the backend
 */
export const useListClients = ({
  page = 0,
  pageSize = 25,
  search = '',
  cursor,
  filter = {},
  sort = {},
  activeTab = 'all',
}) => {
  // Prepare GraphQL variables for cursor-based pagination
  const variables = {
    search: search || '',
    pageSize: pageSize,
  }

  // Only include cursor if it has a value
  if (cursor) {
    variables.cursor = cursor
  }

  return useQuery([fetchClientsList.key, variables], fetchClientsList.query, {
    select: (data) => {
      if (data?.clients) {
        // Transform the response to match the expected format
        return {
          pages: [
            {
              clients: {
                results: data.clients.results.map((item) => item.row),
                pageInfo: {
                  totalRecords: data.clients.pageInfo.totalRecords,
                  currentPage: page,
                  totalPages: Math.ceil(data.clients.pageInfo.totalRecords / pageSize),
                  pageSize: pageSize,
                  hasNextPage: data.clients.pageInfo.hasNextPage,
                  hasPreviousPage: page > 0,
                  startCursor: data.clients.pageInfo.startCursor,
                  endCursor: data.clients.pageInfo.endCursor,
                },
              },
            },
          ],
        }
      }

      return {
        pages: [
          {
            clients: {
              results: [],
              pageInfo: {
                totalRecords: 0,
                currentPage: 0,
                totalPages: 0,
                pageSize: pageSize,
                hasNextPage: false,
                hasPreviousPage: false,
                startCursor: null,
                endCursor: null,
              },
            },
          },
        ],
      }
    },
    keepPreviousData: true, // This provides the "isRefetching" behavior
  })
}

/**
 * Hook to fetch clients list with infinite scrolling support
 */
export const useInfiniteClients = ({
  pageSize = 25,
  search = '',
  filter = {},
  sort = {},
  activeTab = 'all',
}) => {
  return useInfiniteQuery(
    [fetchClientsList.key, 'infinite', { pageSize, search, filter, sort, activeTab }],
    ({ pageParam }) => {
      const variables = {
        search: search || '',
        pageSize: pageSize,
      }

      // Only include cursor if it has a value
      if (pageParam) {
        variables.cursor = pageParam
      }

      return fetchClientsList.query({
        queryKey: [fetchClientsList.key, variables],
      })
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        const pageInfo = lastPage?.clients?.pageInfo

        if (pageInfo?.hasNextPage) {
          return pageInfo.endCursor
        }

        return undefined
      },
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  )
}

/**
 * Hook to fetch a single client by ID
 */
export const useClient = (clientId) => {
  return useQuery([fetchClientById.key, { id: clientId }], fetchClientById.query, {
    enabled: !!clientId,
    select: (data) => {
      return data?.client || null
    },
  })
}

/**
 * Hook to fetch a single client by ID (check-in variant)
 */
export const useGetClient = (clientId, siteId) => {
  return useQuery([getClient.key, { clientId, siteId }], getClient.query, {
    enabled: !!clientId,
    select: (data) => data.client?.client,
  })
}

/**
 * Hook to fetch multiple clients by IDs
 */
export const useClientsByIds = (clientIds, siteId) => {
  const ids = Array.from(new Set(clientIds || [])).filter(Boolean)

  const queries = useQueries(
    ids.map((id) => ({
      queryKey: [getClient.key, { clientId: id, siteId }],
      queryFn: getClient.query,
      enabled: !!id,
      select: (data) => data.client?.client,
    }))
  )

  const isLoading = queries.some((q) => q.isLoading)

  const clientsMap = useMemo(() => {
    const map = {}
    queries.forEach((q, index) => {
      if (q.data) {
        map[ids[index]] = q.data
      }
    })
    return map
  }, [queries, ids])

  return { data: clientsMap, isLoading }
}

/**
 * Hook to fetch client equipment assignments
 */
export const useGetClientAssignments = (clientId, siteId, last, status) => {
  return useQuery(
    [getEquipmentAssignmentsByClientId.key, { clientId, siteId, last, status }],
    getEquipmentAssignmentsByClientId.query,
    {
      enabled: !!clientId && !!siteId,
      select: (data) => data?.equipmentAssignmentsByClientId || [],
    }
  )
}
